import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getToothSpacingSchedules,
  getToothSpacingEntries,
  getToothSpacingEntryDetail,
  createToothSpacingSchedule,
  updateToothSpacingSchedule,
  deleteToothSpacingSchedule,
  type CreateToothSpacingSchedulePayload,
  type UpdateToothSpacingSchedulePayload,
} from '../../../api/tooth-spacing';
import { useAuth } from '../../auth/AuthProvider';

function useRequireToken() {
  const { tokens } = useAuth();
  return () => {
    if (!tokens?.access_token) {
      throw new Error('Authenticated admin session is required.');
    }
    return tokens.access_token;
  };
}

async function invalidateToothSpacingQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  patientId: string,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['tooth-spacing-schedules', patientId] }),
    queryClient.invalidateQueries({ queryKey: ['tooth-spacing-entries', patientId] }),
  ]);
}

export function useToothSpacingSchedules(patientId: string, treatmentId: string) {
  const getToken = useRequireToken();

  return useQuery({
    queryKey: ['tooth-spacing-schedules', patientId, treatmentId],
    queryFn: () => getToothSpacingSchedules(patientId, treatmentId, getToken()),
    enabled: !!patientId && !!treatmentId,
  });
}

export function useToothSpacingEntries(
  patientId: string,
  params?: { status?: string },
) {
  const getToken = useRequireToken();

  return useQuery({
    queryKey: ['tooth-spacing-entries', patientId, params?.status],
    queryFn: () => getToothSpacingEntries(patientId, getToken(), params),
    enabled: !!patientId,
  });
}

export function useCreateToothSpacingSchedule(patientId: string) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateToothSpacingSchedulePayload) =>
      createToothSpacingSchedule(patientId, payload, getToken()),
    onSuccess: () => invalidateToothSpacingQueries(queryClient, patientId),
  });
}

export function useUpdateToothSpacingSchedule(patientId: string, scheduleId: string) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateToothSpacingSchedulePayload) =>
      updateToothSpacingSchedule(patientId, scheduleId, payload, getToken()),
    onSuccess: () => invalidateToothSpacingQueries(queryClient, patientId),
  });
}

export function useToothSpacingEntryDetail(patientId: string, entryId: string | null) {
  const getToken = useRequireToken();
  return useQuery({
    queryKey: ['tooth-spacing-entry', patientId, entryId],
    queryFn: () => getToothSpacingEntryDetail(patientId, entryId!, getToken()),
    enabled: !!patientId && !!entryId,
  });
}

export function useDeleteToothSpacingSchedule(patientId: string) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduleId: string) =>
      deleteToothSpacingSchedule(patientId, scheduleId, getToken()),
    onSuccess: () => invalidateToothSpacingQueries(queryClient, patientId),
  });
}
